import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFetchJson } from '../../misc/use-fetch-json'
import { DerivedSegmentationParams } from '../../../lib/operations/creation-params'
import { useAddSegmentation } from '../../../lib/operations/creation'
import { useStorePlayer } from '../../../modules/player/player-module/store'
import { Context } from '../../../context'
import { saveSectionsMutation } from './mutations'
import { Task } from '../../../types'

export interface Segmentation {
  label: string
  start: number
  end: number
}

export interface UseSections {
  onAddSections(): void
  onChangeSection(value: string, index: number): void
}

interface UseSectionsProps {
  trackId: string
  metadata?: any[]
  segmentationParams: DerivedSegmentationParams | null
  task?: Task
}

export const useSections = ({
  trackId,
  metadata,
  segmentationParams,
  task
}: UseSectionsProps): UseSections => {
  const { i18n } = useLingui()
  const { setData } = useStorePlayer()
  const {
    userToken,
    toast: { add: addToast }
  } = useContext(Context)

  const [url, setUrl] = useState<null | string>(null)
  const { loading, error, result: listOriginal } = useFetchJson({ url })

  const [failed, setFailed] = useState(false)
  const [listModified, setListModified] = useState<null | Segmentation[]>(null)

  const segmentation = useMemo(
    () => task?.summary?.v1?.segmentation,
    [task?.summary]
  )

  const onAddSections = useAddSegmentation(trackId, segmentationParams)

  useEffect(() => {
    const sectionsModified = metadata?.find((i) => i.key === 'sections')
    if (sectionsModified?.value?.length) {
      setListModified(sectionsModified?.value)
      return
    }

    const segmentsPath = segmentation?.segments?.path || null

    setUrl(segmentsPath)
    setFailed(segmentation?.status === 'FAILED')
  }, [metadata, segmentation])

  const onChangeSection = useCallback(
    async (newValue: string, index: number) => {
      if (!newValue.length) {
        return
      }

      const items: any = listModified || listOriginal || []
      items[index].label = newValue
      setListModified(items)

      const result = await saveSectionsMutation({
        sections: items,
        trackId,
        userToken
      })

      if (!result?.updateTrackMetadata) {
        addToast({
          type: 'error',
          closable: true,
          icon: null,
          description: i18n._(t`message_error_improve`)
        })
      }
    },
    [
      i18n,
      listModified,
      listOriginal,
      addToast,
      setListModified,
      trackId,
      userToken
    ]
  )

  const data = useMemo(
    () => listModified || listOriginal || [],
    [listModified, listOriginal]
  )

  const errorState = useMemo(() => error || failed, [error, failed])

  useEffect(() => {
    const updateInProgress = !!segmentation?.updateInProgress
    const sections: any = {
      data,
      empty: !segmentation,
      blocked: data?.length === 0 && !segmentationParams,
      status: updateInProgress
        ? 'update'
        : loading
        ? 'loading'
        : errorState
        ? 'failed'
        : segmentation?.status === 'QUEUED' ||
          segmentation?.status === 'STARTED'
        ? 'processing'
        : segmentation
        ? 'succeeded'
        : undefined
    }

    setData({ sections })
  }, [setData, segmentation, segmentationParams, data, errorState, loading])

  return {
    onAddSections,
    onChangeSection
  }
}
